import useSWR from 'swr';
import { shareService, isApiError } from '@dopomogai/api-client';
import type {
  ShareItem,
  ShareInfo,
  ShareCreatePayload,
  ShareDeletePayload,
  ApiSuccessResponse,
} from '@dopomogai/types';
import { AgentKitError } from '../types';

const SHARES_CACHE_KEY = 'dopomogai/shares/my';

const fetchShares = async (): Promise<ShareItem[]> => {
  const response = await shareService.listSharesForMe();
  if (isApiError(response)) {
    throw new AgentKitError('Failed to load shared resources.', response);
  }
  return response;
};

export const useShares = () => {
  const { data, error, isLoading, mutate } = useSWR<ShareItem[], AgentKitError>(
    SHARES_CACHE_KEY,
    fetchShares,
  );

  const shareResource = async (payload: ShareCreatePayload): Promise<ShareInfo> => {
    const response = await shareService.createShare(payload);
    if (isApiError(response)) {
      throw new AgentKitError('Failed to share resource.', response);
    }
    await mutate();
    return response;
  };

  const revokeShare = async (payload: ShareDeletePayload): Promise<ApiSuccessResponse> => {
    const response = await shareService.deleteShare(payload);
    if (isApiError(response)) {
      throw new AgentKitError('Failed to revoke share.', response);
    }
    await mutate();
    return response;
  };

  return {
    shares: data ?? [],
    isLoading,
    error,
    shareResource,
    revokeShare,
    mutate,
  };
};
